import { NgModule, isDevMode } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { OrderByPipe } from './order-by.pipe';
import { provideLottieOptions } from 'ngx-lottie';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { RootStoreModule } from './root-store';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { AuthInterceptor } from './services/interceptors/auth.interceptor';
import { IMAGE_CONFIG } from '@angular/common';
import { ErrorInterceptor } from './services/interceptors/error.interceptors';
import { AuthModule } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { provideNgxMask } from 'ngx-mask';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

@NgModule({
  declarations: [AppComponent, OrderByPipe],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    RootStoreModule,
    AuthModule.forRoot({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientId,
      authorizationParams: {
        // environment.webUrl (trailing slash included) instead of
        // window.location.origin: `window` does not exist during SSR.
        redirect_uri: environment.webUrl + 'auth/signin',
      },
    }),
    GoogleTagManagerModule.forRoot({
      id: environment.gtmId,
    }),
  ],
  providers: [
    // Enables DOM hydration (client reuses server-rendered DOM instead of a
    // destructive re-render) + event replay. The HTTP transfer cache is enabled
    // by default with hydration, so data fetched during SSR is reused on the
    // client instead of being re-requested.
    //
    // includePostRequests: the default transfer cache only covers GET, so the
    // POST-based reads used above the fold (searchProperty,
    // getArisMarketedProperties) were re-fetched on the client right after SSR.
    // SSR is anonymous and only issues read POSTs, so caching them for a single
    // client replay is safe and removes the duplicate hydration fetch.
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({ includePostRequests: true }),
    ),
    // Root-level HttpClient (was hoisted via the eager PagesModule import).
    // withFetch() is required for SSR; interceptors come from HTTP_INTERCEPTORS below.
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
    provideAnimationsAsync(),
    provideNgxMask(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: IMAGE_CONFIG,
      useValue: {
        disableImageSizeWarning: true,
        disableImageLazyLoadWarning: true,
      },
    },
    // CDK v21 renders overlays in the browser top layer (Popover API) by default,
    // which paints them above all normal-DOM content regardless of z-index — that
    // trapped the ngx-lightbox (a <body> child) beneath the property-gallery dialog.
    // Revert to the pre-v21 z-index:1000 stacking the app's z-index scale assumes.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
